import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/icons/ustaz-kids.png";
import { getModules } from "../services/ModuleService/ModuleService";
import { getAccountInfo } from "../services/AccountService/AccountService";
import TokenService from "../services/token.service";
import { useAuthContext } from "../contexts/AuthContext";
import { getFileByModule } from "../services/FilesService/FilesService";
import { useFilePreview } from "../hooks/useFilePreview";
import FilePreviewModal from "./FilePreviewModal/FilePreviewModal";
import { downloadFile } from "../utils/fileUtils";
import toast from "react-hot-toast";

export default function Navbar() {
  const [menuData, setMenuData] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null); // For download items submenu
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [downloadingItem, setDownloadingItem] = useState(null);
  const { isLoggedIn, logout } = useAuthContext();
  const navigate = useNavigate();

  // Use the reusable file preview hook
  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) return;

    // Load modules
    setLoadingModules(true);
    getModules()
      .then((modules) => {
        const structuredMenu = modules
          .filter((mod) => mod.level === 0)
          .map((parent) => ({
            label: parent.name,
            to: parent.code,
            submenu: parent.children.map((child) => ({
              label: child.name,
              icon: child.icon,
              to: child.code,
            })),
          }));
        setMenuData(structuredMenu);
      })
      .catch((err) => {
        console.error("Failed to fetch modules", err);
      })
      .finally(() => {
        setLoadingModules(false);
      });

    // Load account info
    setLoadingAccount(true);
    getAccountInfo()
      .then((info) => {
        setAccountInfo(info);
      })
      .catch((err) => {
        console.error("Failed to fetch account info", err);
      })
      .finally(() => {
        setLoadingAccount(false);
      });
  }, [isLoggedIn]);

  const handleLogout = () => {
    logout();
    setAccountInfo(null);
    window.location.href = "/";
  };

  const getSafeLink = (to) => {
    const token = TokenService.getLocalAccessToken();
    return token ? `/${to}` : "/no-permission";
  };

  const getUserDisplayName = () => {
    if (!accountInfo) return "";
    const fullName =
      accountInfo.last_name || accountInfo.first_name
        ? `${accountInfo.last_name || ""} ${
            accountInfo.first_name || ""
          }`.trim()
        : null;
    return fullName || accountInfo.email || "";
  };

  const getOrganizationName = () => {
    return accountInfo?.organization?.name || "";
  };

  const fetchFileMeta = async (moduleCode) => {
    try {
      const response = await getFileByModule({
        module_code: moduleCode,
      });

      if (!response?.file) {
        toast.error("Файл табылмады.");
        return null;
      }
      return response.file;
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Файл табылмады немесе қате болды.");
      return null;
    }
  };

  const handlePreviewClick = async (item) => {
    const file = await fetchFileMeta(item.to);
    if (!file) return;

    setSelectedItem(item);
    await openPreview(file.file_url, file.file_name);
    setOpenMenu(null);
    setOpenSubMenu(null);
  };

  const handleDownloadClick = async (item) => {
    setDownloadingItem(item.to);
    try {
      const file = await fetchFileMeta(item.to);
      if (!file) return;

      downloadFile(file.file_url, file.file_name);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Файл жүктеу кезінде қате болды.");
    } finally {
      setDownloadingItem(null);
      setOpenMenu(null);
      setOpenSubMenu(null);
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full bg-white border-b border-gray-200 z-50">
        <div className=" mx-auto flex items-center justify-between h-20 px-6 md:px-20">
          <Link to="/">
            <img src={logo} alt="logo" className="h-9 object-contain mr-20" />
          </Link>

          <div className="hidden md:flex justify-between items-center w-full">
            {/* Menu Items with Loading Skeleton */}
            <ul className="flex space-x-4 text-[#674DA2] items-center">
              {loadingModules ? (
                // Loading skeleton for menu items
                <div className="flex space-x-4 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded-lg w-20"></div>
                  <div className="h-6 bg-gray-200 rounded-lg w-24"></div>
                  <div className="h-6 bg-gray-200 rounded-lg w-16"></div>
                  <div className="h-6 bg-gray-200 rounded-lg w-20"></div>
                </div>
              ) : (
                menuData.map((item) => (
                  <li
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => {
                      setOpenMenu(null);
                      setOpenSubMenu(null);
                    }}
                  >
                    {item.submenu?.length > 0 ? (
                      <span className="hover:text-purple cursor-pointer text-lg flex items-center">
                        {item.label}
                        <svg
                          className={`w-4 h-4 ml-1 transition-transform ${
                            openMenu === item.label ? "rotate-180" : ""
                          }`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75..."
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : (
                      <button
                        onClick={() => navigate(getSafeLink(item.to || "#"))}
                        className="text-lg hover:text-purple cursor-pointer"
                      >
                        {item.label}
                      </button>
                    )}

                    {item.submenu?.length > 0 && openMenu === item.label && (
                      <div className="absolute left-0 top-full bg-white z-50 min-w-max rounded-b-lg shadow-lg">
                        <div className=" grid gap-3 px-6 py-6 sm:grid-cols-2 md:grid-cols-3">
                          {item.submenu.map((sub) => {
                            const isDownload = [
                              "zheke_damu",
                              "atauly_kunder",
                            ].includes(sub.to);
                            const isExternalLink = [
                              "zhazdyq_zhospar",
                              "zhyldyq_zhospar",
                            ].includes(sub.to);

                            if (isDownload) {
                              return (
                                <div
                                  key={sub.label}
                                  className="relative"
                                  onMouseEnter={() => setOpenSubMenu(sub.to)}
                                  onMouseLeave={() => setOpenSubMenu(null)}
                                >
                                  <div className="flex flex-col items-center justify-center bg-[#674DA2] text-white rounded-lg border px-4 pt-4 pb-8 transition-all duration-300 hover:bg-[#5a4190] hover:scale-105 hover:shadow-xl cursor-pointer">
                                    <img
                                      src={
                                        sub.icon || "/assets/default-icon.png"
                                      }
                                      alt={sub.label}
                                      className="w-10 h-10 mb-2"
                                    />
                                    <span className="text-base text-white text-center font-semibold">
                                      {sub.label}
                                    </span>
                                  </div>

                                  {/* Hover submenu for download items */}
                                  {openSubMenu === sub.to && (
                                    <div className="absolute top-0 left-full ml-2 bg-white rounded-lg shadow-xl border p-2 min-w-[120px] z-[100]">
                                      <button
                                        onClick={() => handlePreviewClick(sub)}
                                        disabled={isPreviewLoading}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded flex items-center gap-2 disabled:opacity-50"
                                      >
                                        {isPreviewLoading ? (
                                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                          </svg>
                                        )}
                                        Көру
                                      </button>
                                      <button
                                        onClick={() => handleDownloadClick(sub)}
                                        disabled={downloadingItem === sub.to}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 rounded flex items-center gap-2 disabled:opacity-50"
                                      >
                                        {downloadingItem === sub.to ? (
                                          <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                          </svg>
                                        )}
                                        Жүктеу
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <button
                                key={sub.label}
                                onClick={async () => {
                                  setOpenMenu(null);

                                  if (isExternalLink) {
                                    try {
                                      const response = await getFileByModule({
                                        module_code: sub.to,
                                      });

                                      const url = response?.link?.link;
                                      if (!url) {
                                        toast.error("Сілтеме табылмады.");
                                        return;
                                      }
                                      window.open(url, "_self");
                                    } catch (err) {
                                      toast.error("Қате орын алды.");
                                    }
                                  } else {
                                    navigate(getSafeLink(sub.to || "#"));
                                  }
                                }}
                                className="flex flex-col items-center justify-center bg-[#674DA2] text-white rounded-lg border px-4 pt-4 pb-8  transition-all duration-300 hover:bg-[#5a4190] hover:scale-105 hover:shadow-xl"
                              >
                                <img
                                  src={sub.icon || "/assets/default-icon.png"}
                                  alt={sub.label}
                                  className="w-10 h-10 mb-2"
                                />
                                <span className="text-base text-white text-center font-semibold">
                                  {sub.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>

            {/* User Info and Auth Buttons */}
            {isLoggedIn ? (
              <div className="flex items-center space-x-4">
                {/* User Info with Loading Skeleton */}
                {loadingAccount ? (
                  <div className="text-right animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-24 mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                ) : (
                  accountInfo && (
                    <button
                      onClick={() => navigate("/profile")}
                      className="text-right text-md  transition-colors duration-200"
                    >
                      <div className="font-semibold text-[#674DA2] hover:underline cursor-pointer">
                        {getUserDisplayName()}
                      </div>
                      <div className="text-sm text-gray-600 ">
                        {getOrganizationName()}
                      </div>
                    </button>
                  )
                )}
                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="bg-red-100 text-red-700 font-semibold px-4 py-2 rounded-md hover:bg-red-200 transition"
                >
                  ШЫҒУ
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-[#F3DA6D] text-[#674DA2] font-semibold px-4 py-2 rounded-md hover:bg-yellow-500 transition"
              >
                КІРУ
              </Link>
            )}
          </div>

          <button className="md:hidden" onClick={() => setMobileOpen(true)}>
            <svg
              className="w-7 h-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-50 flex justify-end transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div
          className="flex-1 bg-black/30"
          onClick={() => setMobileOpen(false)}
        />
        <div className="w-2/3 max-w-sm bg-white p-4 overflow-y-auto shadow-xl min-h-full">
          <div className="flex justify-between items-center mb-6">
            {mobileSubmenu ? (
              <>
                <button
                  onClick={() => setMobileSubmenu(null)}
                  className="text-xl"
                >
                  ←
                </button>
                <span className="font-semibold">{mobileSubmenu.label}</span>
                <div className="w-6" />
              </>
            ) : (
              <>
                <img src={logo} alt="logo" className="h-8" />
                <button onClick={() => setMobileOpen(false)}>
                  <svg
                    className="w-6 h-6 text-red-500"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Mobile User Info with Loading */}
          {isLoggedIn && (
            <>
              {loadingAccount ? (
                <div className="mb-6 p-3 bg-gray-50 rounded-lg animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
              ) : (
                accountInfo && (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/profile");
                    }}
                    className="mb-6 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 w-full text-left cursor-pointer"
                  >
                    <div className="font-semibold text-[#674DA2] text-sm">
                      {getUserDisplayName()}
                    </div>
                    <div className="text-xs text-gray-600">
                      {getOrganizationName()}
                    </div>
                  </button>
                )
              )}
            </>
          )}

          {mobileSubmenu ? (
            <ul className="space-y-4">
              {mobileSubmenu.submenu.map((sub, index) => {
                const isDownload = ["zheke_damu", "atauly_kunder"].includes(
                  sub.to
                );
                const isExternalLink = [
                  "zhazdyq_zhospar",
                  "zhyldyq_zhospar",
                ].includes(sub.to);

                if (isDownload) {
                  return (
                    <li key={index} className="border-b pb-2">
                      <div className="text-gray-800 text-base font-medium mb-2">
                        {sub.label}
                      </div>
                      <div className="flex gap-2 pl-2">
                        <button
                          onClick={async () => {
                            await handlePreviewClick(sub);
                            setMobileOpen(false);
                            setMobileSubmenu(null);
                          }}
                          disabled={isPreviewLoading}
                          className="flex-1 bg-blue-500 text-white text-sm py-2 px-3 rounded hover:bg-blue-600 transition disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {isPreviewLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          )}
                          Көру
                        </button>
                        <button
                          onClick={async () => {
                            await handleDownloadClick(sub);
                            setMobileOpen(false);
                            setMobileSubmenu(null);
                          }}
                          disabled={downloadingItem === sub.to}
                          className="flex-1 bg-green-500 text-white text-sm py-2 px-3 rounded hover:bg-green-600 transition disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {downloadingItem === sub.to ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 10v6m0 0l-3-3m3 3l3-3"
                              />
                            </svg>
                          )}
                          Жүктеу
                        </button>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={index}>
                    <button
                      className="block text-left text-gray-800 text-base border-b pb-2 w-full"
                      onClick={async () => {
                        setMobileOpen(false);
                        setMobileSubmenu(null);

                        if (isExternalLink) {
                          try {
                            const response = await getFileByModule({
                              module_code: sub.to,
                            });
                            const url = response?.link?.url;
                            if (!url) return toast.error("Сілтеме табылмады.");
                            window.open(url, "_self");
                          } catch (err) {
                            toast.error("Қате орын алды.");
                          }
                        } else {
                          navigate(getSafeLink(sub.to || "#"));
                        }
                      }}
                    >
                      {sub.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <ul className="space-y-4">
              {/* Mobile Menu Items with Loading */}
              {loadingModules ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-28"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
              ) : (
                menuData.map((item) => (
                  <li key={item.label}>
                    {item.submenu?.length > 0 ? (
                      <button
                        onClick={() => setMobileSubmenu(item)}
                        className="flex justify-between items-center w-full text-left text-gray-800 text-base border-b pb-2"
                      >
                        <span>{item.label}</span>
                        <span>›</span>
                      </button>
                    ) : (
                      <Link
                        to={getSafeLink(item.to || "#")}
                        onClick={() => setMobileOpen(false)}
                        className="block text-gray-800 text-base border-b pb-2"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))
              )}

              {/* Mobile Login/Logout Button */}
              <li>
                {isLoggedIn ? (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      handleLogout();
                    }}
                    className="block w-full text-left text-red-600 text-base border-b pb-2"
                  >
                    ШЫҒУ
                  </button>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block text-[#674DA2] text-base border-b pb-2"
                  >
                    КІРУ
                  </Link>
                )}
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={selectedItem ? selectedItem.label : "Алдын ала қарау"}
      />
    </>
  );
}
